import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AuditFormData, ScoringResult } from "@/pages/AuditFlow";
import AuditProgress from "./AuditProgress";
import { Loader2 } from "lucide-react";

interface AuditStep3Props {
  data: Partial<AuditFormData>;
  onNext: (data: Partial<AuditFormData>) => void;
  onBack: () => void;
  onResults: (result: ScoringResult) => void;
}

export default function AuditStep3({ data, onNext, onBack, onResults }: AuditStep3Props) {
  const { toast } = useToast();
  const [primaryOutcome, setPrimaryOutcome] = useState(data.primary_outcome || "");
  const [aiComfort, setAiComfort] = useState<number>(data.ai_comfort || 3);
  const [budgetTimeframe, setBudgetTimeframe] = useState(data.budget_timeframe || "");
  const [name, setName] = useState(data.name || "");
  const [email, setEmail] = useState(data.email || "");
  const [phone, setPhone] = useState(data.phone || "");
  const [consentGiven, setConsentGiven] = useState(data.consent_given || false);
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsValid(
      !!primaryOutcome &&
      !!budgetTimeframe &&
      !!name &&
      !!email &&
      consentGiven
    );
  }, [primaryOutcome, budgetTimeframe, name, email, consentGiven]);

  const handleSubmit = async () => {
    if (!isValid) return;

    setIsSubmitting(true);

    try {
      const fullData = {
        ...data,
        primary_outcome: primaryOutcome,
        ai_comfort: aiComfort,
        budget_timeframe: budgetTimeframe,
        name,
        email,
        phone,
        consent_given: consentGiven,
      };

      // Calculate score
      const { data: scoreData, error: scoreError } = await supabase.functions.invoke(
        "audit-score",
        {
          body: fullData,
        }
      );

      if (scoreError) {
        console.error("Score calculation error:", scoreError);
        throw new Error("Failed to calculate score");
      }

      const scoringResult: ScoringResult = scoreData;

      // Submit to backend
      const { error: submitError } = await supabase.functions.invoke("audit-submit", {
        body: {
          ...fullData,
          ...scoringResult,
        },
      });

      if (submitError) {
        console.error("Submission error:", submitError);
        throw new Error("Failed to submit audit");
      }

      // Track analytics
      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "audit_complete", {
          event_category: "conversion",
          event_label: `Tier: ${scoringResult.tier}`,
          value: scoringResult.score,
        });
      }

      onNext(fullData);
      onResults(scoringResult);

      toast({
        title: "Audit Complete!",
        description: "Your results are ready. Check your email for the full report.",
      });
    } catch (error) {
      console.error("Error submitting audit:", error);
      toast({
        title: "Error",
        description: "Failed to submit audit. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <AuditProgress currentStep={3} totalSteps={3} />

      <div className="bg-dark-card rounded-2xl p-8 md:p-12 border border-border/20 mt-8">
        <h2 className="text-3xl font-bold text-foreground mb-3">
          Priorities & Contact
        </h2>
        <p className="text-light-gray/80 mb-8">
          Final questions to personalize your results
        </p>

        <div className="space-y-8">
          {/* Primary Outcome */}
          <div>
            <Label htmlFor="primary_outcome" className="text-foreground mb-2 block">
              Top outcome for next 90 days? *
            </Label>
            <Select value={primaryOutcome} onValueChange={setPrimaryOutcome}>
              <SelectTrigger id="primary_outcome" className="bg-background border-border">
                <SelectValue placeholder="Select primary outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="More booked calls">More booked calls</SelectItem>
                <SelectItem value="Faster response times">
                  Faster response times
                </SelectItem>
                <SelectItem value="Higher conversion">Higher conversion</SelectItem>
                <SelectItem value="Better data / insights">
                  Better data / insights
                </SelectItem>
                <SelectItem value="Reduce manual workload">
                  Reduce manual workload
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* AI Comfort */}
          <div>
            <Label className="text-foreground mb-4 block">
              Team comfort with AI tools? *
            </Label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-medium-gray min-w-[100px]">
                Beginner
              </span>
              <Slider
                value={[aiComfort]}
                onValueChange={(value) => setAiComfort(value[0])}
                min={1}
                max={5}
                step={1}
                className="flex-1"
              />
              <span className="text-sm text-medium-gray min-w-[100px] text-right">
                Expert
              </span>
            </div>
            <div className="text-center mt-2">
              <span className="text-2xl font-bold text-cyan">{aiComfort}</span>
              <span className="text-sm text-light-gray/70"> / 5</span>
            </div>
          </div>

          {/* Budget/Timeframe */}
          <div>
            <Label htmlFor="budget_timeframe" className="text-foreground mb-2 block">
              Budget / Timeframe? *
            </Label>
            <Select value={budgetTimeframe} onValueChange={setBudgetTimeframe}>
              <SelectTrigger id="budget_timeframe" className="bg-background border-border">
                <SelectValue placeholder="Select budget and timeframe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Exploring only">Exploring only</SelectItem>
                <SelectItem value="<$5k / 30-60 days">
                  &lt;$5k / 30-60 days
                </SelectItem>
                <SelectItem value="$5-25k / 60-90 days">
                  $5-25k / 60-90 days
                </SelectItem>
                <SelectItem value="$25k+ / 90+ days">$25k+ / 90+ days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Contact Information */}
          <div className="border-t border-border/20 pt-8 mt-8">
            <h3 className="text-xl font-semibold text-foreground mb-6">
              Get Your Results
            </h3>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-foreground mb-2 block">
                  Name *
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="bg-background border-border"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-foreground mb-2 block">
                  Work Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@company.com"
                  className="bg-background border-border"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-foreground mb-2 block">
                  Phone (Optional)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="bg-background border-border"
                />
              </div>

              <div className="flex items-start space-x-2 pt-4">
                <Checkbox
                  id="consent"
                  checked={consentGiven}
                  onCheckedChange={(checked) => setConsentGiven(checked as boolean)}
                />
                <label
                  htmlFor="consent"
                  className="text-sm text-light-gray/90 leading-tight cursor-pointer"
                >
                  I agree to receive my report and follow-up from ArqeraAI.{" "}
                  <Link to="/privacy" className="text-cyan hover:underline">
                    Privacy policy
                  </Link>
                  .
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-10">
          <Button
            onClick={onBack}
            variant="outline"
            className="border-border text-foreground hover:bg-dark-card"
            disabled={isSubmitting}
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="bg-cyan hover:bg-cyan-muted text-deep-navy font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              "View My Results"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}